import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Cpu, Layers, MessageSquare, Radio, ShieldCheck, Workflow,
} from 'lucide-react';
import Faq from './ui/Faq';
import CtaBand from './ui/CtaBand';
import Seo from './ui/Seo';

/**
 * /product — the communication platform itself.
 *
 * Deliberately free of vertical language. Specific applications get their own
 * pages; this one describes the platform: agents at the edge that judge what
 * equipment reports, devices that exchange what they know, and people reached
 * on the channels they already use.
 *
 * Says nothing about what ships when. Availability is a conversation.
 */

const PARTICIPANTS = [
  {
    Icon: Cpu,
    label: 'Agents',
    title: 'Equipment that reports a judgement, not a reading',
    body:
      'An agent runs alongside the hardware and interprets what it is reporting — locally, without waiting on a server. What leaves the device is a conclusion worth acting on rather than another number for a dashboard.',
  },
  {
    Icon: Radio,
    label: 'Devices',
    title: 'Machines that exchange what they know',
    body:
      'Devices within range share context directly, so one machine can act on what another has observed without a central system brokering every exchange. Local decisions stay local, and stay immediate.',
  },
  {
    Icon: MessageSquare,
    label: 'People',
    title: 'The right person, on a channel they already use',
    body:
      'A conclusion reaches whoever needs to act, through the channels your organisation already relies on — and when they respond, the response returns attached to the matter it concerned.',
  },
];

const PLATFORM = [
  {
    Icon: Workflow,
    title: 'Conditions become actions',
    body:
      'Define what matters and what should follow. A threshold crossed, a pattern recognised, a state changed — each can raise work, notify a person, or trigger a process in another system.',
  },
  {
    Icon: Layers,
    title: 'Multi-tenant by design',
    body:
      'Accounts nest. An organisation manages its own customers, each isolated, each with capabilities enabled independently. Built this way from the beginning, because it is how the platform is delivered.',
  },
  {
    Icon: ShieldCheck,
    title: 'Compliance handled centrally',
    body:
      'Reaching people at scale carries regulatory obligation. Registration, consent capture and auditable records are managed by the platform rather than assembled separately by every organisation using it.',
  },
];

const FAQS = [
  {
    q: 'What kind of equipment does this apply to?',
    a: 'Anything that reports its condition — mobile or fixed, mechanical or electronic. The agent interprets telemetry; it does not require a particular manufacturer or protocol.',
  },
  {
    q: 'Why decide at the edge rather than centrally?',
    a: 'Latency and reliability. A decision made beside the equipment does not depend on a connection being available at the moment it matters, and it does not queue behind everything else a central system is processing.',
  },
  {
    q: 'How does this reach the systems we already run?',
    a: 'Through authenticated HTTP APIs, and through direct integrations where they exist. A conclusion is only useful if it arrives in the system your teams already work in, so the platform is built to deliver it there rather than become another place people have to check.',
  },
  {
    q: 'How is it delivered?',
    a: 'Through implementation partners and consulting practices, under their brand and their commercial relationship. If you are one of them, the partners page covers how that works.',
  },
  {
    q: 'How do we find out whether it fits?',
    a: 'By talking to us. What makes sense depends on what you operate, what already reports, and what your organisation does when something changes — which is a conversation rather than a page.',
  },
];

const Product = () => {
  const navigate = useNavigate();

  return (
    <div>
      <Seo
        path="/product"
        title="The platform"
        description="A communication platform for AI agents, connected devices and the people who depend on them. Agents interpret what equipment reports, devices exchange context directly, and the right person is reached on a channel they already use."
      />

      <section className="cp-hero">
        <div className="cp-container">
          <div style={{ maxWidth: 820 }}>
            <span className="cp-eyebrow">
              <Cpu size={12} style={{ verticalAlign: '-1px', marginRight: 6 }} />
              The platform
            </span>
            <h1 className="cp-h1">
              A communication platform for agents, devices and the people who depend on them.
            </h1>
            <p className="cp-lede">
              Equipment produces signal constantly, and almost none of it reaches a person while
              it still matters. CoPlaid places an agent alongside the hardware to judge what is
              worth raising, lets devices share that context directly with one another, and
              delivers it to whoever needs to act.
            </p>
            <div className="cp-hero-actions">
              <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate('/contact')}>
                Talk to us <ArrowRight size={18} />
              </button>
              <button className="cp-btn cp-btn--ghost cp-btn--lg" onClick={() => navigate('/partners')}>
                For partners
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <div style={{ maxWidth: 780, marginBottom: 48 }}>
            <span className="cp-eyebrow">How it works</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>Three participants, one conversation.</h2>
            <p className="cp-lede">
              Close the distance between a machine observing something and a person acting on it,
              and a substantial share of operational cost closes with it.
            </p>
          </div>
          <div className="cp-grid-3">
            {PARTICIPANTS.map(({ Icon, label, title, body }) => (
              <div className="cp-card" key={label}>
                <div className="cp-card-icon"><Icon size={22} /></div>
                <span className="cp-eyebrow" style={{ margin: '6px 0 0', display: 'block' }}>{label}</span>
                <h3 className="cp-card-title" style={{ marginTop: 10 }}>{title}</h3>
                <p className="cp-card-text">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section">
        <div className="cp-container">
          <div style={{ maxWidth: 780, marginBottom: 48 }}>
            <span className="cp-eyebrow">Underneath</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>What the platform carries for you.</h2>
            <p className="cp-lede">
              The parts that are tedious to build once and unreasonable to build repeatedly.
            </p>
          </div>
          <div className="cp-grid-3">
            {PLATFORM.map(({ Icon, title, body }) => (
              <div className="cp-card" key={title}>
                <div className="cp-card-icon"><Icon size={22} /></div>
                <h3 className="cp-card-title" style={{ marginTop: 10 }}>{title}</h3>
                <p className="cp-card-text">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <div style={{ maxWidth: 780, marginBottom: 40 }}>
            <h2 className="cp-h2">Common questions.</h2>
          </div>
          <Faq items={FAQS} />
        </div>
      </section>

      <CtaBand
        title="Tell us what you operate."
        sub="We will be direct about whether this applies to your organisation, and about what it would take."
        primary={{ label: 'Talk to us', to: '/contact' }}
        secondary={{ label: 'For partners', to: '/partners' }}
      />
    </div>
  );
};

export default Product;
